/**
 * Module: lib/copilot/authedFetch
 * Responsibility: Wrap window.fetch with the merchant dashboard JWT and
 *   retry-on-401 behaviour. On a 401 response we call the deduped
 *   `refreshTokenDeduped()` (which wraps `refreshExpiredIdToken(true)` with a
 *   single-flight in-flight promise), then re-read the auth cookie and retry
 *   the original request ONCE with the fresh token.
 *
 * Why this lives here (and not next to the global axios interceptor):
 *   The copilot client uses fetch (not axios) because EventSource/streaming
 *   responses require fetch + ReadableStream. The dashboard's existing
 *   refresh-on-401 logic in `lib/core/api-client.ts` only wires axios.
 *   This helper gives the copilot codepath the same guarantee for fetch.
 *
 * Retry policy: at most ONE retry per request. If refresh fails or returns
 *   false, or the access cookie didn't actually rotate after the refresh,
 *   we surface the original 401 Response to the caller. Callers can decide
 *   how to render that final 401.
 *
 * Side-effect note: `refreshExpiredIdToken` calls `redirectToLogin()` from
 *   its own catch path when the refresh endpoint itself errors. That
 *   redirect happens BEFORE this helper sees the result, so even though
 *   THIS helper never explicitly redirects, a refresh failure can still
 *   navigate the user away. This matches the behaviour of every other
 *   refresh-on-401 path in the dashboard (axios interceptor, fetcher).
 *
 * Concurrency: deduped via {@link refreshTokenDeduped}. When several copilot
 *   requests (e.g. /copilot/suggestions, /copilot/conversations,
 *   /copilot/chat) hit 401 within the same tick, only ONE underlying
 *   refresh fires; the rest await the same promise and then re-read the
 *   rotated cookie. Without this, parallel refreshes race on cookie
 *   rotation and silently force a logout.
 */

import Cookies from 'js-cookie'

import { constants } from '../utils/constants'
import { refreshTokenDeduped } from './sharedRefresh'

function readAuthCookie(): string {
  return Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? ''
}

function withAuthHeader(init: RequestInit, token: string): RequestInit {
  return {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`
    }
  }
}

/**
 * Like window.fetch, but injects the merchant JWT as a Bearer token and
 * transparently refreshes + retries once on a 401 response.
 *
 * @param input  Fetch input (URL or Request).
 * @param init   Standard RequestInit. Any `Authorization` value in
 *               `init.headers` is overwritten with the current cookie value.
 * @returns      The final Response. If the retry was attempted and the
 *               second response is also non-2xx, that second response is
 *               returned. If refresh failed (threw, returned false, or the
 *               cookie didn't rotate), the original 401 is returned.
 */
export async function authedFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const initialToken = readAuthCookie()
  const firstRes = await fetch(input, withAuthHeader(init, initialToken))

  if (firstRes.status !== 401) {
    return firstRes
  }

  // 401 — try one refresh + retry cycle, deduped across concurrent callers.
  let refreshed = false
  try {
    refreshed = await refreshTokenDeduped()
  } catch {
    return firstRes
  }
  if (!refreshed) return firstRes

  const refreshedToken = readAuthCookie()
  // Defensive: if the cookie didn't actually rotate (refresh succeeded but
  // returned the same access token), there's no point retrying — the next
  // call would just 401 again.
  if (!refreshedToken || refreshedToken === initialToken) {
    return firstRes
  }

  return fetch(input, withAuthHeader(init, refreshedToken))
}
